import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle } from 'lucide-react';
import { config } from '@/config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  keep: {
    title: 'Stay on the list?',
    prompt: "Tap below and you'll keep getting stories and photos from the Outer Banks.",
    button: 'Yes, keep me',
    done: "You're in. Thanks for sticking around.",
    doneStatus: 'kept',
    opposite: 'remove',
    oppositeLabel: 'Take me off the list instead',
  },
  remove: {
    title: 'Leave the list?',
    prompt: "Tap below and you won't get any more emails from me.",
    button: 'Yes, remove me',
    done: "You're off the list.",
    doneStatus: 'removed',
    opposite: 'keep',
    oppositeLabel: 'Keep me on the list instead',
  },
};

// The token the admin's "Send test" email puts in its buttons
// (backend/routes/reconfirm.py). It belongs to no subscriber, so the page
// runs as a preview: same screens, nothing sent to the server.
const TEST_TOKEN = 'test-preview';

// /list/keep?t=TOKEN and /list/remove?t=TOKEN — the legacy re-confirmation
// links. Loading the page only LOOKS UP the subscriber; nothing changes until
// the person clicks the button, because email link scanners open (and even
// run the JavaScript on) every link in an email.
function ListChoice({ choice }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const isTest = token === TEST_TOKEN;
  const copy = COPY[choice];
  const [state, setState] = useState('loading'); // loading | ready | saving | done | invalid
  const [frequency, setFrequency] = useState('weekly');
  const [savingFrequency, setSavingFrequency] = useState(false);

  useEffect(() => {
    if (token === TEST_TOKEN) {
      setState('ready');
      return undefined;
    }
    let cancelled = false;
    setState('loading');
    axios.get(`${BACKEND_URL}/api/reconfirm/status`, { params: { t: token } })
      .then((resp) => {
        if (cancelled) return;
        setFrequency(resp.data.frequency);
        // Already chose this (e.g. clicked twice) — just show the result.
        setState(resp.data.status === COPY[choice].doneStatus ? 'done' : 'ready');
      })
      .catch(() => { if (!cancelled) setState('invalid'); });
    return () => { cancelled = true; };
  }, [token, choice]);

  const confirm = async () => {
    if (isTest) {
      setState('done');
      return;
    }
    setState('saving');
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/reconfirm/choice`, { token, choice });
      setFrequency(resp.data.frequency);
      setState('done');
    } catch (error) {
      setState(error.response?.status === 404 ? 'invalid' : 'ready');
    }
  };

  const chooseFrequency = async (value) => {
    if (value === frequency) return;
    if (isTest) {
      setFrequency(value);
      return;
    }
    const previous = frequency;
    setFrequency(value);
    setSavingFrequency(true);
    try {
      await axios.post(`${BACKEND_URL}/api/reconfirm/frequency`, { token, frequency: value });
    } catch (error) {
      setFrequency(previous);
    } finally {
      setSavingFrequency(false);
    }
  };

  const frequencyOption = (value, title, detail) => (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: '10px', textAlign: 'left', cursor: 'pointer',
      padding: '12px 14px', marginBottom: '8px', borderRadius: '6px',
      border: `1px solid ${frequency === value ? '#1a1a1a' : '#d0d0d0'}`,
      background: frequency === value ? '#f8f8f8' : '#fff'
    }}>
      <input type="radio" name="frequency" value={value} checked={frequency === value}
        disabled={savingFrequency} onChange={() => chooseFrequency(value)} style={{ marginTop: '4px' }} />
      <span>
        <strong>{title}</strong>
        <span style={{ display: 'block', fontSize: '14px', color: '#666' }}>{detail}</span>
      </span>
    </label>
  );

  const linkStyle = { color: '#1a1a1a', fontWeight: '600' };

  return (
    <div className="public-page" style={{
      backgroundImage: `url(${config.backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}>
      <div className="public-container" style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        textAlign: 'center'
      }}>
        {isTest && (
          <div style={{
            background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px',
            padding: '10px 14px', marginBottom: '20px', fontSize: '14px', color: '#92400e'
          }} data-testid="list-choice-test-banner">
            <strong>Test preview.</strong> This is what your readers will see. Nothing is saved.
          </div>
        )}
        {state === 'loading' ? (
          <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
        ) : state === 'invalid' ? (
          <>
            <XCircle style={{ width: '56px', height: '56px', color: '#999', margin: '0 auto 16px' }} />
            <h1>Hmm, that link didn't work</h1>
            <p>It may be old or got cut off in your email.</p>
            <p style={{ marginTop: '16px' }}>
              Want to hear from me? <Link to="/subscribe" style={linkStyle}>Sign up here</Link> — it only takes a second.
            </p>
          </>
        ) : state === 'done' ? (
          <>
            <CheckCircle style={{ width: '56px', height: '56px', color: '#28a745', margin: '0 auto 16px' }} />
            <h1 data-testid="list-choice-done">{copy.done}</h1>
            {choice === 'keep' ? (
              <>
                <p style={{ marginBottom: '16px' }}>How often would you like to hear from me?</p>
                <div style={{ maxWidth: '360px', margin: '0 auto' }}>
                  {frequencyOption('weekly', 'Weekly roundup', "Every Monday, the week's new stories in one email.")}
                  {frequencyOption('daily', 'Every new story', "Each story in your inbox as soon as it's published.")}
                </div>
                <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 16px' }}>
                  {savingFrequency ? 'Saving…' : 'Saved automatically.'}
                </p>
                <p><Link to="/" style={linkStyle}>Catch up on recent stories</Link></p>
              </>
            ) : (
              <p>The door's always open at <Link to="/subscribe" style={linkStyle}>theobxbeachbum.com/subscribe</Link>.</p>
            )}
            <p style={{ marginTop: '24px', fontSize: '14px', color: '#666' }}>
              Changed your mind?{' '}
              <Link to={`/list/${copy.opposite}?t=${encodeURIComponent(token)}`} style={linkStyle}>{copy.oppositeLabel}</Link>
            </p>
          </>
        ) : (
          <>
            <h1>{copy.title}</h1>
            <p>{copy.prompt}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={confirm}
              disabled={state === 'saving'}
              style={{ marginTop: '16px', minWidth: '220px', opacity: state === 'saving' ? 0.7 : 1 }}
              data-testid="list-choice-confirm"
            >
              {state === 'saving' ? 'Saving…' : copy.button}
            </button>
            <p style={{ marginTop: '24px', fontSize: '14px', color: '#666' }}>
              <Link to={`/list/${copy.opposite}?t=${encodeURIComponent(token)}`} style={linkStyle}>{copy.oppositeLabel}</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default ListChoice;
